import {action} from 'mobx';
import {IWebRtcDetector} from '@techsee/techsee-client-infra/lib/services/DetectWebRtcService';
import {ClientInfo} from '@techsee/techsee-common/lib/data-contracts/ClientInfo';
import {PlatformType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {getBrowserInfo} from '@techsee/techsee-client-infra/lib/shared/browser-info';
import {
    GeneralSettings,
    InviteMethodSettings,
    IPasswordSettings,
    ISessionSettings,
    VirtualBackgroundItem
} from '../../models/AccountSettings';
import {SessionService} from '../../services/Session/SessionService';
import {SmsService} from '../../services/SmsService';
import {SmsService as SmsServiceInfra} from '@techsee/techsee-client-infra/lib/services/smsService';
import {RoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {AgentInfoController} from '../../layouts/main-layout/agent-info-menu/controller';
import {AppState, IAppUser} from '../../app.state';
import {getRootStore} from '../../app.bootstrap';
import {InviteFlowManager} from './invite.flow.manager';
import {InviteEventsLogger} from './invite.flow.logger';
import {InviteStateManager} from './invite.persister';
import {WizardLeftBarController} from './left-bar/controller';
import {AgentPerformanceController} from '../../components/agent-performance/controller';
import {
    RolesConstant,
    VideoFilterType,
    VideoBackgroundOptions
} from '@techsee/techsee-common/lib/constants/account.constants';
import {LookupNumberController} from './invite-wizard/components/lookup-number/controller';
import {IRouteStore} from '../../router/routing.infra';

import get from 'lodash/get';
import {AccountSwitchController} from '../../layouts/main-layout/account-switch/controller';
import {SelectVideoPublisherController} from '../../components/select-video-publisher/controller';
import {SimpleModalController} from '../../components/simple-modal/controller';
import {getDashboardLanguage} from '../helper/general.helper';
import {SESSION_TAGS_LIMITS} from '@techsee/techsee-common/lib/constants/session-tags.constants';

export class InviteStores implements IRouteStore {
    private readonly _currentAccount: any;
    private readonly _currentUser: any;
    private _accountSettings: any;
    private readonly _stateHelper: any;

    public localizationService: any;
    public inviteFlowManager!: InviteFlowManager;
    public mainLayoutController: any;
    public leftBarController: any;
    public agentInfoController: any;
    public agentSettingsController: any;
    public historyConsoleFilterController: any;
    public wifiAnalyzerFilterController: any;
    public vjHistoryFilterController: any;
    public agentPerformanceController: any;
    public selectVideoPublisherController: any;
    constructor(accountData: any, currentUser: any, stateHelper: any) {
        this._currentAccount = accountData;
        this._currentUser = currentUser;
        this._stateHelper = stateHelper;
    }

    public async initStores(): Promise<void> {
        const {
            appState,
            applicationService,
            localizationService,
            shortUrlService,
            mobileClientURL,
            redirectionService,
            roomService,
            observationsService,
            dbVideoRecordingsService,
            domUtilsService,
            socketManager,
            browserUtilsService,
            eventsLogService,
            webRtcDetector,
            accountSocketService,
            authExpiryService,
            emailService,
            environmentService,
            generalApiService,
            authService
        } = getRootStore();

        this.updateAppState(appState);
        this.updateAccountSettings();

        if (this._accountSettings.inviteMethodSettings.allowAddSessionTags) {
            await this._loadSessionTags(appState.accountId);
        }

        localizationService.setAccountData(appState.accountId, appState.dashboardLanguage);

        const inviteFlowLogger = new InviteEventsLogger(eventsLogService, appState.accountId, appState.agentId, {
            keepCustomerMobile:
                this._accountSettings.generalSettings && this._accountSettings.generalSettings.keepCustomerMobile,
            keepCustomerEmailAddress:
                this._accountSettings.generalSettings && this._accountSettings.generalSettings.keepCustomerEmailAddress
        });
        const clientInfo: ClientInfo = await this.getClientInfo(webRtcDetector);
        const sessionService = new SessionService(
            clientInfo,
            roomService,
            shortUrlService,
            this._accountSettings.sessionSettings,
            socketManager.sessionSocket,
            inviteFlowLogger,
            environmentService
        );

        const smsServiceInfra = new SmsServiceInfra(
            () => getRootStore().authService.getAuthToken() || '',
            getRootStore().urlConfigService.get('API_URL')
        );
        const inviteRoomService = new RoomService(
            getRootStore().urlConfigService.get('API_URL'),
            () => getRootStore().authService.getAuthToken() || ''
        );
        const smsService = new SmsService(
            smsServiceInfra,
            inviteRoomService,
            this._accountSettings.smsSettings,
            inviteFlowLogger,
            browserUtilsService,
            this._accountSettings.inviteMethodSettings.allowSendByWhatsApp
        );
        const inviteStateManager = new InviteStateManager(browserUtilsService);

        await localizationService.init();

        let agentPerformanceController = null;
        let selectVideoPublisherController = null;

        try {
            if (
                this._accountSettings.inviteMethodSettings.enableAgentPerformance &&
                [RolesConstant.USER, RolesConstant.MANAGER].includes(this._currentUser.role) &&
                !browserUtilsService.getFromSessionStorage('shouldNotDisplayAgentPerformance')
            ) {
                const enableCustomerSurvey =
                    get(this._currentAccount, 'settings.customerSurvey.enabled') &&
                    get(this._currentAccount, 'protectedSettings.allowCustomerSurveys');

                const enableAgentSurvey =
                    get(this._currentAccount, 'settings.enableSurveys') &&
                    get(this._currentAccount, 'protectedSettings.feedbackSurveys');

                agentPerformanceController = new AgentPerformanceController(
                    localizationService.translate,
                    getRootStore().generalApiService,
                    this._currentUser.role === RolesConstant.USER,
                    browserUtilsService,
                    authExpiryService,
                    eventsLogService,
                    enableCustomerSurvey as boolean,
                    enableAgentSurvey as boolean,
                    this._currentUser
                );
                agentPerformanceController.setElementWrapper('app-middle-area');

                agentPerformanceController.init();
            }
        } catch (e) {
            console.warn('AgentPerformanceController initialization skipped:', e);
        }

        if (
            this._currentUser.role === RolesConstant.FIELD_SERVICES_ENGINEER &&
            get(this._currentAccount, 'protectedSettings.enableFieldServices')
        ) {
            selectVideoPublisherController = new SelectVideoPublisherController(localizationService.translate);
        }

        appState.setAgentSettings(
            this._accountSettings.passwordSettings,
            this._accountSettings.generalSettings,
            this._currentAccount.settings.language || 'en_US'
        );

        const agentSettingsController = getRootStore().createAgentSettingsController();

        const lookupNumberController = new LookupNumberController(
            localizationService.translate,
            eventsLogService,
            this._accountSettings.inviteMethodSettings.keepCustomerMobile
        );

        const unauthorizedModal = new SimpleModalController(
            'unauthorized-modal',
            '',
            localizationService.translate('REACT.INVITE.VIEW.UNAUTHORIZED_ERROR_CONTENT'),
            '',
            localizationService.translate('REACT.INVITE.VIEW.UNAUTHORIZED_ERROR_BUTTON')
        );

        const preDataCollectionWarningModal = new SimpleModalController(
            'pre-data-collection-warning-modal',
            localizationService.translate('REACT.INVITE.VIEW.PRE_DATA_COLLECTION.WARNING_TITLE'),
            localizationService.translate('REACT.INVITE.VIEW.PRE_DATA_COLLECTION.WARNING_CONTENT'),
            localizationService.translate('REACT.INVITE.VIEW.PRE_DATA_COLLECTION.WARNING_NO'),
            localizationService.translate('REACT.INVITE.VIEW.PRE_DATA_COLLECTION.WARNING_YES')
        );

        const inviteFlowManager = new InviteFlowManager(
            this._accountSettings,
            inviteStateManager,
            inviteFlowLogger,
            localizationService,
            sessionService,
            smsService,
            redirectionService,
            dbVideoRecordingsService,
            roomService,
            observationsService,
            appState.accountId,
            browserUtilsService,
            agentSettingsController,
            this._currentUser,
            domUtilsService,
            mobileClientURL,
            appState,
            agentPerformanceController,
            lookupNumberController,
            accountSocketService,
            emailService,
            selectVideoPublisherController,
            unauthorizedModal,
            environmentService,
            generalApiService,
            authService,
            this._stateHelper,
            preDataCollectionWarningModal
        );

        const agentInfoController = new AgentInfoController(
            applicationService,
            this._accountSettings.generalSettings,
            localizationService.translate,
            redirectionService,
            agentSettingsController,
            appState
        );

        const switchUserController = new AccountSwitchController(
            applicationService,
            this._currentUser,
            this._currentAccount.companyName
        );

        const leftBarController = new WizardLeftBarController(
            localizationService.translate,
            environmentService,
            this._accountSettings.generalSettings,
            this._currentUser,
            agentInfoController,
            switchUserController
        );

        await inviteFlowManager.onReady;

        this.localizationService = localizationService;
        this.inviteFlowManager = inviteFlowManager;
        this.mainLayoutController = inviteFlowManager.mainLayoutController;
        this.leftBarController = leftBarController;
        this.agentInfoController = agentInfoController;
        this.agentSettingsController = agentSettingsController;
        this.historyConsoleFilterController = inviteFlowManager.historyConsoleFilterController;
        this.wifiAnalyzerFilterController = inviteFlowManager.wifiAnalyzerFilterController;
        this.vjHistoryFilterController = inviteFlowManager.vjHistoryFilterController;
        this.agentPerformanceController = agentPerformanceController;
        this.selectVideoPublisherController = selectVideoPublisherController;
    }

    dispose(): void {
        if (this.inviteFlowManager) {
            this.inviteFlowManager.dispose();
        }
    }

    @action
    private updateAppState(appState: AppState): void {
        appState.accountId = this._currentAccount._id;
        appState.dashboardLanguage = getDashboardLanguage(this._currentAccount);
        appState.accountName = this._currentAccount.companyName;
        appState.setAppUser(this._currentUser as IAppUser);
    }

    //#region Account Settings Mapping (TODO: Create "SettingsAdapter" to encapsulate settings mapping)

    // HY-1: fetch the account's session-tags list (objects [{value}]) and expose the values
    // as dropdown options. Non-fatal — on failure the wizard simply shows no tags.
    private async _loadSessionTags(accountId: string): Promise<void> {
        try {
            const {tags, maxPerSession} = await getRootStore().sessionTagsService.getAvailableTags(accountId);

            this._accountSettings.inviteMethodSettings.sessionTags = tags;
            this._accountSettings.inviteMethodSettings.maxSessionTags = maxPerSession;
        } catch (err) {
            this._accountSettings.inviteMethodSettings.sessionTags = [];
            this._accountSettings.inviteMethodSettings.maxSessionTags = SESSION_TAGS_LIMITS.maxPerSession;
        }
    }

    private updateAccountSettings(): void {
        this._accountSettings = {
            inviteMethodSettings: this.getAccInviteMethodSettings(),
            generalSettings: this.getAccGeneralSettings(),
            sessionSettings: this.getSessionSettings(),
            passwordSettings: this.getPasswordSettings(),
            smsSettings: this._currentAccount.protectedSettings.smsSettings,
            v3: this._currentAccount.protectedSettings.v3,

            v2_1: this._currentAccount.protectedSettings.v2_1,
            showRoomIdInUrl: this._currentAccount.protectedSettings.showRoomIdInUrl
        };
    }

    private getAccInviteMethodSettings(): InviteMethodSettings | any {
        return {
            addCountryCodeToSms: this._currentAccount.protectedSettings.smsSettings.addCountryCodeToSms,
            whatsapp: {
                addCountryCode: get(this._currentAccount, 'protectedSettings.whatsappSettings.addCountryCode', false),
                selectedCountries: get(
                    this._currentAccount,
                    'protectedSettings.whatsappSettings.selectedCountries',
                    this._currentAccount.protectedSettings.selectedCountries
                ),
                maxLengthWithoutCountryCode: get(
                    this._currentAccount,
                    'protectedSettings.whatsappSettings.maxLengthWithoutCountryCode',
                    this._currentAccount.protectedSettings.smsSettings.smsMaxLengthWithoutCountryCode
                )
            },
            phoneCountryCodes: this._currentAccount.protectedSettings.selectedCountries,
            customerIdValidationRules: {
                display: this._currentAccount.settings.customerId.display,
                displayForInviteByCode: this._currentAccount.settings.customerId.displayForInviteByCode,
                displayForInviteByEmail: this._currentAccount.settings.customerId.displayForInviteByEmail,
                minLength: this._currentAccount.settings.customerId.minLength,
                maxLength: this._currentAccount.settings.customerId.maxLength
            },
            disablePhoneNumberForInviteByCode: this._currentAccount.settings.disablePhoneNumberForInviteByCode,
            selectVideoPublisher: this._currentAccount.protectedSettings.selectVideoPublisher,
            disableEmailForInviteByCode: this._currentAccount.settings.disableEmailForInviteByCode,
            multiLanguage: this._currentAccount.settings.multiLanguage,
            clientLanguages: this._currentAccount.settings.clientLanguages,
            showRoomIdInUrl: this._currentAccount.protectedSettings.showRoomIdInUrl,
            dashboardLanguage: this._currentAccount.settings.language || 'en_US',
            keepCustomerMobile: this._currentAccount.protectedSettings.keepCustomerMobile,
            defaultCountryCode: this._currentAccount.settings.countryCode,
            startWithModes: this._currentAccount.protectedSettings.startWithModes,
            accountOfflineSessions: this._currentAccount.settings.accountOfflineSessions,
            accountPendingRoomSessions: this._currentAccount.protectedSettings.warmTransfer.enable,
            showAlertsToSelectedGroups: this._currentAccount.settings.showAlertsToSelectedGroups,
            pendingRoomShowAlertsToSelectedGroups:
                this._currentAccount.protectedSettings.warmTransfer.showAlertsToSelectedGroups,
            selectedGroupsForOfflineAlerts: this._currentAccount.settings.selectedGroupsForOfflineAlerts,
            selectedGroupsForPendingRoomAlerts:
                this._currentAccount.protectedSettings.warmTransfer.selectedGroupsForAlerts,
            allowSearchOfflineSessionsById: this._currentAccount.protectedSettings.allowSearchOfflineSessionsById,
            allowDesktopSharing: this._currentAccount.protectedSettings.desktopSharing.enabled,
            showWizard: this._currentAccount.settings.showWizard,
            enableAgentPerformance: this._currentAccount.protectedSettings.enableAgentPerformance,
            enableQuickLaunch: this._currentAccount.protectedSettings.enableQuickLaunch,
            allowExternalCustomerDetails: this._currentAccount.protectedSettings.allowExternalCustomerDetails,
            audioStartMode: this._currentAccount.protectedSettings.audioStartMode,
            mediaType: this._currentAccount.protectedSettings.mediaType,
            enableSessionToolbar: this._currentAccount.protectedSettings.enableSessionToolbar,
            visualJourney: {
                enabled: get(this._currentAccount, 'protectedSettings.visualJourney.enabled', false),
                useFlowManagementV2: get(
                    this._currentAccount,
                    'protectedSettings.visualJourney.useFlowManagementV2:',
                    false
                ),
                available: get(this._currentAccount, 'protectedSettings.visualJourney.available', [])
            },
            virtualBackground: {
                enabledBlurAgent: get(
                    this._currentAccount,
                    'protectedSettings.faceMeet.virtualBackground.enabledBlurAgent',
                    false
                ),
                enabledImageReplacementAgent: get(
                    this._currentAccount,
                    'protectedSettings.faceMeet.virtualBackground.enabledImageReplacementAgent',
                    false
                ),
                default: get(
                    this._currentAccount,
                    'protectedSettings.faceMeet.virtualBackground.defaultBackground',
                    VideoFilterType.NONE
                )
            },
            coBrowsing: {
                enabled: get(this._currentAccount, 'protectedSettings.coBrowsing.enabled', false),
                defaultURL: get(this._currentAccount, 'protectedSettings.coBrowsing.defaultURL', ''),
                surflyWidgetKey: get(this._currentAccount, 'protectedSettings.coBrowsing.surflyWidgetKey', '')
            },
            allowInviteBySms: this._currentAccount.settings.allowInviteBySms,
            allowSendByEmail: this._currentAccount.settings.allowSendByEmail,
            allowSendByWhatsApp: this._currentAccount.settings.allowSendByWhatsApp,
            emailSendBy: get(this._currentAccount, 'protectedSettings.emailSendBy'),
            observationEnabled: get(this._currentAccount, 'protectedSettings.enableSilentObserve'),
            desktopShareOnlyMode: get(this._currentAccount, 'protectedSettings.desktopShareOnlyMode'),
            referralConfiguration: get(this._currentAccount, 'protectedSettings.liveSdk.referralConfiguration'),
            liveSdk: get(this._currentAccount, 'protectedSettings.liveSdk'),
            v3: {
                enabled: get(this._currentAccount, 'protectedSettings.v3.enabled')
            },
            groups: this._currentAccount.groups,
            v2_1: {
                enabled: get(this._currentAccount, 'protectedSettings.v2_1.enabled')
            },
            agentDefaultCamera: get(this._currentAccount, 'protectedSettings.faceMeet.agentDefaultCamera'),
            videoResolution: get(this._currentAccount, 'protectedSettings.videoResolution'),
            mediaServiceType: get(this._currentAccount, 'protectedSettings.mediaServiceType'),
            enableMediaServiceTypeBySession: get(
                this._currentAccount,
                'protectedSettings.enableMediaServiceTypeBySession'
            ),
            agentStartSessionWithVideo: get(
                this._currentAccount,
                'protectedSettings.faceMeet.agentStartSessionWithVideo'
            ),
            copilot: get(this._currentAccount, 'protectedSettings.copilot'),
            enableAgentWifiScan:
                (this._currentAccount.protectedSettings?.connectivityGuru?.enableAgentWifiScan &&
                    this._currentAccount.protectedSettings.liveSdk.enableNativeFullAssist) ??
                false,
            allowAddSessionTags: get(this._currentAccount, 'protectedSettings.allowAddSessionTags', false),
            sessionTags: [],
            maxSessionTags: SESSION_TAGS_LIMITS.maxPerSession
        };
    }

    private getSessionSettings(): ISessionSettings {
        return {
            cameraModeOnly: this._currentAccount.settings.cameraModeOnly,
            chromeDetection: this._currentAccount.protectedSettings.chromeDetection,
            fallbackModeNotification: this._currentAccount.protectedSettings.fallbackModeNotification
        };
    }

    private getAccGeneralSettings(): GeneralSettings {
        const {enableHistory, enableHistorySearch, enableUserHistorySearch} = this._currentAccount.protectedSettings;

        const isCurrentUserEligibleForHistorySearch = [
            RolesConstant.USER,
            RolesConstant.KNOWLEDGE_EXPERT,
            RolesConstant.FIELD_SERVICES_ENGINEER
        ].includes(this._currentUser.role);
        const isEligibleForHistorySearch =
            enableHistory && enableHistorySearch && (enableUserHistorySearch || !isCurrentUserEligibleForHistorySearch);

        return {
            enableLinkToAdmin: this._currentAccount.protectedSettings.enableLinkToAdmin,
            logoUrl: this._currentAccount.settings.logoUrl,
            nativeAppId: this._currentAccount.protectedSettings.liveSdk.nativeAppId,
            enableNativeFullAssist: this._currentAccount.protectedSettings.liveSdk.enableNativeFullAssist,
            enableNewInviteWifiStep: this._currentAccount.protectedSettings.enableNewInviteWifiStep,
            enableNewInviteSpeakersStep: this._currentAccount.protectedSettings.enableNewInviteSpeakersStep,
            isHistorySearchEnabled: isEligibleForHistorySearch,
            isVJHistorySearchEnabled:
                this._currentAccount.protectedSettings.interactionSummary.enableVJInteractionSummarySearch,
            enableWifiScan: this._currentAccount.protectedSettings?.connectivityGuru?.enableWifiScan,
            enableAgentWifiScan:
                (this._currentAccount.protectedSettings?.connectivityGuru?.enableAgentWifiScan &&
                    this._currentAccount.protectedSettings.liveSdk.enableNativeFullAssist) ??
                false,
            localImageSaving: this._currentAccount.protectedSettings.localImageSaving,
            emailImageSharing: this._currentAccount.protectedSettings.emailImageSharing,
            subDomain: get(this._currentAccount, 'subDomain.name'),
            domainZoneSuffix: get(this._currentAccount, 'subDomain.domainZoneSuffix'),
            guidPrefix: this._currentAccount.guidPrefix,
            keepCustomerMobile: this._currentAccount.protectedSettings.keepCustomerMobile,
            keepCustomerEmailAddress: this._currentAccount.protectedSettings.keepCustomerEmailAddress,
            reportsDateFormat: this._currentAccount.protectedSettings.reportsDateFormat,
            verboseLogging: get(this._currentAccount, 'protectedSettings.verboseLogging.enabled'),
            enableMultiLanguage: this._currentAccount.protectedSettings.allowAgentToChangeHisLanguage,
            appLauncher: this._currentAccount.protectedSettings.appLauncher?.invitePage,
            enabledVirtualBackgroundInFaceMeet:
                this._currentAccount.protectedSettings.faceMeet?.virtualBackground?.enabledInFaceMeet,
            enabledImageReplacementAgent:
                this._currentAccount.protectedSettings.faceMeet?.virtualBackground?.enabledImageReplacementAgent,
            enabledBlurAgent: this._currentAccount.protectedSettings.faceMeet?.virtualBackground?.enabledBlurAgent,
            defaultBackground: this._currentAccount.protectedSettings.faceMeet?.virtualBackground?.defaultBackground,
            accountType: this._currentAccount.accountType,
            interactionSummaryHistoryPerPage:
                this._currentAccount.protectedSettings.interactionSummary.historySearchPerPage,
            historyConsole: this._currentAccount.protectedSettings.historyConsole
        };
    }

    private getPasswordSettings(): IPasswordSettings {
        return {
            ...this._currentAccount.settings.passwordSettings,
            allowCustomPasswordPolicy: this._currentAccount.protectedSettings.allowCustomPasswordPolicy
        };
    }

    private async getClientInfo(detectRTC: IWebRtcDetector): Promise<ClientInfo> {
        const webRtcInfo = await detectRTC.getWebRtcInfo();
        const clientInfo: ClientInfo = {
            platformType: PlatformType.dashboard,
            clientVersion: CLIENT_VERSION,
            browserInfo: getBrowserInfo(),
            webRtcInfo: webRtcInfo
        };

        return clientInfo;
    }

    //#endregion
}
